import React, { useCallback, useEffect, useState } from "react";
import {
  ClaudeMessage,
  ConversationDetails,
  ConversationMeta,
} from "@/utils/axios/claudeUtils.ts";

import ClaudeChatSidebar from "./parts/ClaudeChatSidebar.tsx";
import ClaudeChatMain from "./parts/ClaudeChatMain.tsx";
import ClaudeChatLayout from "./ClaudeChatLayout.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// import { useClaudeSocket } from "./hooks/useClaudeSocket";
import { useClaudeQueries } from "./hooks/useClaudeQueries.ts";

const ClaudeChat: React.FC = () => {
  // Component state
  const [messages, setMessages] = useState<ClaudeMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<
    string | undefined
  >();
  const [conversationTitle, setConversationTitle] = useState<string>("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  // Get QueryClient instance
  const queryClient = useQueryClient();

  // Custom hooks for API queries and WebSocket
  const {
    getConversations,
    getConversationHistory,
    createConversation,
    deleteConversationById,
    clearConversations,
    renameConversationTitle,
    sendMessage,
  } = useClaudeQueries();

  // Configure WebSocket handlers
  // const {
  //   connectSocket,
  //   joinConversation,
  //   leaveConversation,
  //   notifyCreateConversation,
  //   notifyDeleteConversation,
  //   notifyClearConversations,
  //   notifyRenameConversation,
  // } = useClaudeSocket({
  //   onMessageReceived: (data) => {
  //     if (data.conversationId === activeConversationId) {
  //       const newMessage: ClaudeMessage = {
  //         role: "assistant",
  //         content: data.message,
  //         timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
  //       };
  //       setMessages((prev) => [...prev, newMessage]);
  //       setIsLoading(false);
  //     }
  //
  //     // Update the cached data without triggering a refetch
  //     queryClient.setQueryData(
  //       ["conversation", data.conversationId],
  //       (oldData: any) => {
  //         if (!oldData) return null;
  //
  //         const updatedMessages = [
  //           ...oldData.messages,
  //           {
  //             role: "assistant",
  //             content: data.message,
  //             timestamp: data.timestamp || new Date().toISOString(),
  //           },
  //         ];
  //
  //         return {
  //           ...oldData,
  //           messages: updatedMessages,
  //         };
  //       }
  //     );
  //
  //     // Also invalidate the conversations list since the last message might have changed
  //     queryClient.invalidateQueries({ queryKey: ["conversations"] });
  //   },
  //   onConversationUpdated: () => {
  //     // Only invalidate the list, not individual conversations
  //     queryClient.invalidateQueries({ queryKey: ["conversations"] });
  //   },
  //   onConversationDeleted: (data) => {
  //     if (data.conversationId === activeConversationId) {
  //       setMessages([]);
  //       setActiveConversationId(undefined);
  //     }
  //
  //     // Remove the conversation from cache
  //     queryClient.removeQueries({
  //       queryKey: ["conversation", data.conversationId],
  //     });
  //     queryClient.invalidateQueries({ queryKey: ["conversations"] });
  //   },
  //   onProcessingStatus: (data) => {
  //     setIsLoading(data.isProcessing);
  //   },
  //   onError: (data) => {
  //     if (data.conversationId === activeConversationId) {
  //       setMessages((prev) => [
  //         ...prev,
  //         {
  //           role: "assistant",
  //           content: "Désolé, une erreur est survenue: " + data.message,
  //           timestamp: new Date(),
  //         },
  //       ]);
  //       setIsLoading(false);
  //     }
  //   },
  // });

  // Query for conversations list, with optimized refetching
  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations"],
    queryFn: getConversations,
    // Data stays fresh longer to reduce network calls
    staleTime: 1000 * 60 * 10, // 10 minutes
    // Don't auto-refetch this data on window focus or reconnect
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // But still retry on error
    retry: 2,
  });

  // Query for conversation history
  const { data: conversationDetails, isLoading: isHistoryLoading } =
    useQuery<ConversationDetails | null>({
      queryKey: ["conversation", activeConversationId],
      queryFn: () =>
        activeConversationId
          ? getConversationHistory(activeConversationId)
          : null,
      // Keep data fresh longer since WebSocket will update as needed
      staleTime: 1000 * 60 * 10, // 10 minutes
      // Only run this query when we have an active conversation
      enabled: !!activeConversationId,
      // Don't auto-refetch on window focus since WebSocket handles updates
      refetchOnWindowFocus: false,
      // Cache data for longer period
      gcTime: 1000 * 60 * 60, // 1 hour
      // Prevent refetches when switching between conversations by keeping history in cache
      // Note: In TanStack Query v5, placeholderData replaces keepPreviousData
      placeholderData: (prev) => prev,
    });

  // Mutations with optimistic updates
  const createConversationMutation = useMutation({
    mutationFn: createConversation,
    onSuccess: (data) => {
      if (data?.conversation) {
        setActiveConversationId(data.conversation.id);
        setMessages([]);
        // notifyCreateConversation(data.conversation.id);

        // Add new conversation to the cache immediately
        queryClient.setQueryData(
          ["conversations"],
          (old: ConversationMeta[] = []) => {
            return [data.conversation, ...old];
          }
        );
      }
    },
  });

  const deleteConversationMutation = useMutation({
    mutationFn: deleteConversationById,
    // Optimistically update UI before the API call completes
    onMutate: async (conversationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["conversations"] });

      // Snapshot the current value
      const previousConversations = queryClient.getQueryData(["conversations"]);

      // Optimistically remove this conversation
      queryClient.setQueryData(
        ["conversations"],
        (old: ConversationMeta[] = []) => {
          return old.filter((c) => c.id !== conversationId);
        }
      );

      // Also remove from cache
      queryClient.removeQueries({ queryKey: ["conversation", conversationId] });

      return { previousConversations };
    },
    onSuccess: (_, conversationId) => {
      if (conversationId === activeConversationId) {
        setMessages([]);
        setActiveConversationId(undefined);
      }
      // notifyDeleteConversation(conversationId);

      // If in mobile view and we delete the last conversation, show the sidebar
      if (isMobileView && conversations.length <= 1) {
        setShowSidebar(true);
      }
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(
          ["conversations"],
          context.previousConversations
        );
      }
    },
  });

  const clearConversationsMutation = useMutation({
    mutationFn: clearConversations,
    // Optimistically clear all conversations
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["conversations"] });

      // Snapshot the current value
      const previousConversations = queryClient.getQueryData(["conversations"]);

      // Optimistically set empty array
      queryClient.setQueryData(["conversations"], []);

      return { previousConversations };
    },
    onSuccess: () => {
      setMessages([]);
      setActiveConversationId(undefined);
      // notifyClearConversations();
      setShowSidebar(true);

      // Clear all conversation caches
      queryClient.removeQueries({ queryKey: ["conversation"] });
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(
          ["conversations"],
          context.previousConversations
        );
      }
    },
  });

  const renameConversationMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      renameConversationTitle(id, title),
    // Optimistically update the conversation title
    onMutate: async ({ id, title }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["conversations"] });

      // Snapshot the current values
      const previousConversations = queryClient.getQueryData(["conversations"]);
      const previousConversation = queryClient.getQueryData([
        "conversation",
        id,
      ]);

      // Optimistically update the title in the list
      queryClient.setQueryData(
        ["conversations"],
        (old: ConversationMeta[] = []) => {
          return old.map((c) => (c.id === id ? { ...c, title } : c));
        }
      );

      // Also update in the individual conversation if it's cached
      if (previousConversation) {
        queryClient.setQueryData(["conversation", id], (old: any) => {
          if (!old) return null;
          return { ...old, title };
        });
      }

      return { previousConversations, previousConversation };
    },
    onSuccess: (_, variables) => {
      setConversationTitle(variables.title);
      // notifyRenameConversation(variables.id, variables.title);
    },
    onError: (_, variables, context) => {
      // If the mutation fails, roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(
          ["conversations"],
          context.previousConversations
        );
      }

      if (context?.previousConversation) {
        queryClient.setQueryData(
          ["conversation", variables.id],
          context.previousConversation
        );
      }
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: ({
      message,
      conversationId,
    }: {
      message: string;
      conversationId?: string;
    }) => sendMessage(message, conversationId),
    onMutate: async ({ message, conversationId }) => {
      // If we have a conversation ID, optimistically update the messages
      if (conversationId) {
        // Add the user message to the cached conversation
        queryClient.setQueryData(
          ["conversation", conversationId],
          (old: any) => {
            if (!old) return null;

            const newMessage = {
              role: "user",
              content: message,
              timestamp: new Date().toISOString(),
            };

            return {
              ...old,
              messages: [...old.messages, newMessage],
            };
          }
        );
      }
    },
    onSuccess: (response, variables) => {
      // If this is a new conversation, update the active conversation
      if (!activeConversationId && response?.conversationId) {
        setActiveConversationId(response.conversationId);
        setConversationTitle(response.title || "Nouvelle conversation");

        // Add the new conversation to the list
        queryClient.setQueryData(
          ["conversations"],
          (old: ConversationMeta[] = []) => {
            const newConversation = {
              id: response.conversationId,
              title: response.title || "Nouvelle conversation",
            };
            return [newConversation, ...old];
          }
        );

        // Initialize cache for this conversation
        queryClient.setQueryData(["conversation", response.conversationId], {
          conversationId: response.conversationId,
          title: response.title || "Nouvelle conversation",
          messages: [
            {
              role: "user",
              content: variables.message,
              timestamp: new Date().toISOString(),
            },
          ],
        });
      }

      // Fallback for direct API response if WebSocket doesn't deliver
      const responseTimeout = setTimeout(() => {
        if (isLoading && response?.message) {
          console.log("Using direct API response as fallback");
          const assistantMessage: ClaudeMessage = {
            role: "assistant",
            content: response.message,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, assistantMessage]);
          setIsLoading(false);

          // Update cache for this conversation
          const convId = response.conversationId || activeConversationId;
          if (convId) {
            queryClient.setQueryData(["conversation", convId], (old: any) => {
              if (!old) return null;

              return {
                ...old,
                messages: [
                  ...old.messages,
                  {
                    role: "assistant",
                    content: response.message,
                    timestamp: new Date().toISOString(),
                  },
                ],
              };
            });
          }
        }
      }, 4000);

      return () => clearTimeout(responseTimeout);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Désolé, une erreur est survenue. Veuillez réessayer.",
          timestamp: new Date(),
        },
      ]);
      setIsLoading(false);
    },
  });

  // Initialize WebSocket connection
  // useEffect(() => {
  //   connectSocket();
  // }, [connectSocket]);

  // Update messages from conversation details when they change
  useEffect(() => {
    if (
      conversationDetails &&
      "messages" in conversationDetails &&
      "title" in conversationDetails
    ) {
      setMessages(
        conversationDetails.messages.map((msg) => ({
          ...msg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        }))
      );
      setConversationTitle(conversationDetails.title);
    }
  }, [conversationDetails]);

  // Join conversation channel when activeConversationId changes
  useEffect(() => {
    if (activeConversationId) {
      // joinConversation(activeConversationId);

      // In mobile view, hide sidebar when conversation is selected
      if (isMobileView) {
        setShowSidebar(false);
      }
    } else if (conversations.length > 0) {
      // Load most recent conversation if available
      setActiveConversationId(conversations[0].id);
    }

    // Cleanup: leave conversation channel
    return () => {
      if (activeConversationId) {
        // leaveConversation(activeConversationId);
      }
    };
  }, [
    activeConversationId,
    conversations,
    isMobileView,
    // joinConversation,
    // leaveConversation,
  ]);

  // Check mobile view
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);

      // Automatically hide sidebar on mobile if conversation is active
      if (isMobile && activeConversationId) {
        setShowSidebar(false);
      } else if (!isMobile) {
        setShowSidebar(true);
      }
    };

    // Check on load
    checkIsMobile();

    // Add resize listener
    window.addEventListener("resize", checkIsMobile);

    // Cleanup listener
    return () => window.removeEventListener("resize", checkIsMobile);
  }, [activeConversationId]);

  // Handle creating a new conversation
  const handleNewConversation = async () => {
    createConversationMutation.mutate();
  };

  // Handle deleting a conversation
  const handleDeleteConversation = async (id: string) => {
    deleteConversationMutation.mutate(id);
  };

  // Handle clearing all conversations
  const handleClearAllConversations = async () => {
    if (
      window.confirm(
        "Es-tu sûr de vouloir supprimer toutes tes conversations ?"
      )
    ) {
      clearConversationsMutation.mutate();
    }
  };

  // Handle renaming a conversation
  const handleRenameConversation = async (newTitle: string) => {
    if (activeConversationId && newTitle.trim()) {
      renameConversationMutation.mutate({
        id: activeConversationId,
        title: newTitle.trim(),
      });
    }
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (id: string) => {
      setActiveConversationId(id);

      // In mobile view, hide sidebar when conversation is selected
      if (isMobileView) {
        setShowSidebar(false);
      }
    },
    [isMobileView]
  );

  // Handle back to list (for mobile)
  const handleBackToList = useCallback(() => {
    if (isMobileView) {
      setShowSidebar(true);
    }
  }, [isMobileView]);

  // Handle sending a message
  const handleSendMessage = async (input: string) => {
    if (!input.trim()) return;

    // Add user message locally
    const userMessage: ClaudeMessage = {
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Send message to API
    sendMessageMutation.mutate({
      message: input,
      conversationId: activeConversationId,
    });
  };

  // Prepare components for layout
  const sidebarComponent = (
    <ClaudeChatSidebar
      conversations={conversations}
      activeConversationId={activeConversationId}
      onSelectConversation={handleSelectConversation}
      onNewConversation={handleNewConversation}
      onDeleteConversation={handleDeleteConversation}
      onClearAllConversations={handleClearAllConversations}
      isMobileView={isMobileView}
    />
  );

  const mainComponent = (
    <ClaudeChatMain
      messages={messages}
      isLoading={isLoading || isHistoryLoading}
      conversationTitle={conversationTitle}
      showSidebar={showSidebar}
      onToggleSidebar={toggleSidebar}
      onSendMessage={handleSendMessage}
      onRenameConversation={handleRenameConversation}
      onBackToList={handleBackToList}
      isMobileView={isMobileView}
    />
  );

  return (
    <ClaudeChatLayout
      isMobileView={isMobileView}
      showSidebar={showSidebar}
      sidebarComponent={sidebarComponent}
      mainComponent={mainComponent}
    />
  );
};

export default ClaudeChat;
